package org.teddapp.project.ui.adaptive

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Classe de largeur de fenêtre. Un seul écran par section s'adapte à la place
 * d'une version Mac et d'une version téléphone.
 *
 * - [Compact] : téléphone en portrait. Les blocs s'empilent, la navigation est en bas,
 *   les panneaux secondaires deviennent des feuilles.
 * - [Medium] : tablette ou fenêtre étroite. Navigation en rail, liste et détail côte à côte,
 *   panneaux tertiaires en feuille.
 * - [Expanded] : ordinateur. Tout est visible en colonnes.
 */
enum class WindowClass {
    Compact, Medium, Expanded;

    companion object {
        val COMPACT_MAX: Dp = 600.dp
        val MEDIUM_MAX: Dp = 1100.dp

        fun of(width: Dp): WindowClass = when {
            width < COMPACT_MAX -> Compact
            width < MEDIUM_MAX -> Medium
            else -> Expanded
        }
    }
}

/** Classe de fenêtre fournie par [WithWindowClass] à tout ce qu'il contient. */
val LocalWindowClass = staticCompositionLocalOf { WindowClass.Expanded }

/** Mesure la largeur disponible et fournit la classe de fenêtre au contenu. */
@Composable
fun WithWindowClass(
    modifier: Modifier = Modifier,
    content: @Composable (WindowClass) -> Unit,
) {
    BoxWithConstraints(modifier = modifier) {
        val windowClass = WindowClass.of(maxWidth)
        CompositionLocalProvider(LocalWindowClass provides windowClass) {
            content(windowClass)
        }
    }
}
